import React from "react";
import {FlexCol} from "../generic/Containers";
import {BCHeader2, Body2, Subtitle1} from "../generic/BCHeaders";
import {BCPrimaryButton} from "../generic/BCButtons";
import {useGlobalState} from "../../GlobalState";
import "./HowItWorksComponent.css";

type StepPosition = 'start' | 'middle' | 'end';

interface Step {
    number: number;
    title: string;
    description: string;
}

const steps: Step[] = [
    {
        number: 1,
        title: "Go Beyond Basic Coding",
        description: "Debugging, APIs, Git, & more"
    },
    {
        number: 2,
        title: "Build a Real Portfolio",
        description: "Turn coding tutorials into job-ready projects"
    },
    {
        number: 3,
        title: "Resume & Cover Letter",
        description: "Stand out and get recruiter replies"
    },
    {
        number: 4,
        title: "Interview Confidence",
        description: "Learn to ace coding and behavioral interviews"
    },
    {
        number: 5,
        title: "Get Hired",
        description: "Land offers as a professional dev"
    }
];

const StepCircle: React.FC<{number: number}> = ({number}) => (
    <div className="step-circle">
        <span className="step-circle-number">{number}</span>
    </div>
);

const DesktopStep: React.FC<{step: Step, position: StepPosition}> = ({step, position}) => (
    <div className="desktop-step">
        <div className="desktop-step-top">
            <div className={`desktop-step-line ${position === 'start' ? 'hidden-line' : ''}`} />
            <StepCircle number={step.number} />
            <div className={`desktop-step-line ${position === 'end' ? 'hidden-line' : ''}`} />
        </div>
        <div className="desktop-step-text">
            <Subtitle1 
                text={step.title} 
                textAlign="center"
                fontWeight={600}
                fontSize={1.2}
            />
            <Body2
                text={step.description}
                textAlign="center"
                style={{color: 'var(--secondary-text-color)'}}
            />
        </div>
    </div>
);

const MobileStep: React.FC<{step: Step, position: StepPosition}> = ({step, position}) => (
    <div className="mobile-step">
        <div className={`mobile-step-line ${position === 'start' ? 'hidden-line' : ''}`} />
        <div className="mobile-step-content">
            <StepCircle number={step.number} />
            <div className="mobile-step-text">
                <Subtitle1 
                    text={step.title} 
                    textAlign="left"
                    fontWeight={600}
                    fontSize={1.4}
                />
                <Body2 
                    text={step.description} 
                    textAlign="left"
                    style={{color: 'var(--secondary-text-color)'}}
                />
            </div>
        </div>
        <div className={`mobile-step-line ${position === 'end' ? 'hidden-line' : ''}`} />
    </div>
);

const getPosition = (index: number, total: number): StepPosition => {
    if (index === 0) return 'start';
    if (index === total - 1) return 'end';
    return 'middle';
};

const DesktopSteps: React.FC = () => (
    <div className="desktop-steps-container">
        {steps.map((step, index) => (
            <DesktopStep 
                key={index} 
                step={step} 
                position={getPosition(index, steps.length)} 
            />
        ))}
    </div>
);

const MobileSteps: React.FC = () => (
    <div className="mobile-steps-container">
        {steps.map((step, index) => (
            <MobileStep 
                key={index} 
                step={step} 
                position={getPosition(index, steps.length)} 
            />
        ))}
    </div>
);

export const HowItWorksComponent: React.FC = () => {
    const { setShowAuthModal, setAuthModalMode } = useGlobalState();

    return (
        <FlexCol
            className={"how-it-works-component"}
            widthPercent={100}
            align="center"
            pHorizontal={2}
        >
            <BCHeader2 
                text={"How Beyond Code Gets You Hired"} 
                textAlign="center"
                className="landing-section-header"
            />
            <div className="how-it-works-desktop">
                <DesktopSteps />
            </div>
            <div className="how-it-works-mobile">
                <MobileSteps />
            </div>
            <BCPrimaryButton
                text="JOIN FOR FREE"
                className={'landing-primary-cta-button'}
                style={{ marginTop: '2.5em' }}
                onClick={() => {
                    setAuthModalMode('register');
                    setShowAuthModal(true);
                }}
            />
        </FlexCol>
    );
};
